import { useCallback } from 'react';

import { type AirActionTrackingLocation, useTrackCanvasMoved } from '@air/analytics';
import {
  CopyPlusIcon,
  LinkIcon,
  Maximize2Icon,
  MinusIcon,
  MoveBoardIcon,
  PencilIcon,
  Trash2Icon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  DeleteCanvasConfirmationModal,
  type LeaveAfterCanvasDelete,
} from '~/components/Canvas/DeleteCanvasConfirmationModal';
import { EditCanvasDetailsModal } from '~/components/Canvas/EditCanvasDetailsModal';
import { MoveCanvasToBoardModal } from '~/components/Canvas/MoveCanvasToBoardModal/MoveCanvasToBoardModal';
import { DuplicateCanvasModal } from '~/components/Modals/DuplicateCanvas';
import { SharePrivateCanvasModal } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasModal';
import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type CanvasListItem } from '~/swr-hooks/canvases/types';
import { usePrivateCanvasesUpdateCanvasMutation } from '~/swr-hooks/canvases/useUpdateCanvas';
import {
  canCreateCanvasInBoard,
  canDeleteCanvasInBoard,
  canEditCanvasInBoard,
  canViewCanvasInBoard,
} from '~/utils/permissions/boardPermissions';
import { canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';
import {
  canCreateCanvasInWorkspace,
  canDeleteCanvasFromWorkspace,
  canEditCanvasInWorkspace,
  canManageCanvasesInWorkspace,
  canViewCanvasInWorkspace,
} from '~/utils/permissions/workspacePermissions';

type GetCanvasMenuOptionsCallbackParams = {
  item: CanvasListItem;
  trackLocation: AirActionTrackingLocation;
  boardId?: string;
  /** Opening board for Open/Duplicate URLs. `null` = workspace (do not use hook/`canvas.boardId`). Omit to use the hook `boardId`. */
  openingBoardId?: string | null;
  /** Set by the dedicated canvas page. List and split-panel menus omit it. */
  leaveAfterDelete?: LeaveAfterCanvasDelete;
};

/** `boardId` is permissions context. Nested editor URLs use `openingBoardId` (or this hook `boardId` when opening is omitted), not canvas membership. */
export const useGetCanvasMenuOptionsCallback = (boardId?: string) => {
  const { selectCanvas } = useSelectedCanvasId();
  const [showEditCanvasDetailsModal] = useAirModal(EditCanvasDetailsModal);
  const [showDeleteCanvasConfirmationModal] = useAirModal(DeleteCanvasConfirmationModal);
  const [showMoveCanvasToBoardModal] = useAirModal(MoveCanvasToBoardModal);
  const [showSharePrivateCanvasModal] = useAirModal(SharePrivateCanvasModal);
  const [showDuplicateCanvasModal] = useAirModal(DuplicateCanvasModal);
  const { currentWorkspace } = useCurrentWorkspace();
  const { mutateAsync: updateCanvas } = usePrivateCanvasesUpdateCanvasMutation();
  const { trackCanvasMoved } = useTrackCanvasMoved();
  const { showToast } = useToasts();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { data: account } = useAccountContext();

  const getCanvasMenuOptionsCallback = useCallback(
    ({
      item: canvas,
      trackLocation,
      boardId: boardIdOverride,
      openingBoardId,
      leaveAfterDelete,
    }: GetCanvasMenuOptionsCallbackParams) => {
      const options: DropdownMenuOption[] = [];
      const effectiveBoardId = boardIdOverride ?? boardId;
      const navigationBoardId = openingBoardId === undefined ? boardId : (openingBoardId ?? undefined);

      const boardPermissions = effectiveBoardId ? getBoardPermissions(effectiveBoardId) : undefined;
      const isOwner = !!account?.id && canvas.createdBy === account.id;
      const canManageCanvases = canManageCanvasesInWorkspace(workspacePermissions);

      // View rules: 194 (board, no ownership), 195/196 (workspace, canManageCanvases or own)
      const canView = effectiveBoardId
        ? canViewCanvasInBoard(boardPermissions)
        : canViewCanvasInWorkspace(workspacePermissions) && (canManageCanvases || isOwner);

      if (canView) {
        options.push({
          id: 'open',
          label: 'Open',
          type: 'item',
          prefix: <Maximize2Icon size="sm" />,
          onSelect: () => {
            selectCanvas(
              canvas.id,
              navigationBoardId
                ? { boardInfo: { boardId: navigationBoardId, boardTitle: canvas.boardTitle ?? undefined } }
                : undefined,
            );
          },
        });
      }

      // Share rules: board — shortUrl.canView on board; workspace — own canvas or canManageCanvases + shortUrl.canView
      // Modal handles all sub-states (view-only, create, manage) internally
      const canShare = effectiveBoardId
        ? canViewShortUrl(boardPermissions)
        : canViewShortUrl(workspacePermissions) && (isOwner || canManageCanvases);

      if (canShare) {
        options.push({
          id: 'share',
          label: 'Share a link',
          type: 'item',
          prefix: <LinkIcon size="sm" />,
          onSelect: () => {
            showSharePrivateCanvasModal({ canvasId: canvas.id, trackLocation });
          },
        });
      }

      // Edit rules (199, 200) are all own-only
      const canEdit =
        isOwner &&
        (effectiveBoardId ? canEditCanvasInBoard(boardPermissions) : canEditCanvasInWorkspace(workspacePermissions));
      const canDuplicate =
        canView &&
        (effectiveBoardId
          ? canCreateCanvasInBoard(boardPermissions)
          : canCreateCanvasInWorkspace(workspacePermissions));

      if (canEdit) {
        if (options.length > 0) {
          options.push({ id: 'move-to-separator', type: 'separator' });
        }

        options.push({
          id: 'move-to',
          label: 'Move to...',
          prefix: <MoveBoardIcon size="sm" />,
          onSelect: () => {
            showMoveCanvasToBoardModal({
              canvasIds: [canvas.id],
              currentBoardId: canvas.boardId,
              trackLocation,
            });
          },
          type: 'item',
        });

        options.push({
          id: 'rename',
          label: 'Rename',
          type: 'item',
          prefix: <PencilIcon size="sm" />,
          onSelect: () => {
            showEditCanvasDetailsModal({ canvasId: canvas.id });
          },
        });
      }

      if (canDuplicate) {
        options.push({
          id: 'duplicate-canvas',
          label: 'Duplicate Canvas',
          type: 'item',
          prefix: <CopyPlusIcon size="sm" />,
          onSelect: () => {
            showDuplicateCanvasModal({
              canvas,
              trackLocation,
              boardId: navigationBoardId,
            });
          },
        });
      }

      // Remove from board is Canvas.update (edit action, own-only rule 199)
      const canRemoveFromBoard = effectiveBoardId ? canEditCanvasInBoard(boardPermissions) && isOwner : false;
      // Delete: rules 190/191 (canManageCanvases) OR 192/193 (own)
      const canDelete = effectiveBoardId
        ? canDeleteCanvasInBoard(boardPermissions) && (canManageCanvases || isOwner)
        : canDeleteCanvasFromWorkspace(workspacePermissions) && (canManageCanvases || isOwner);

      if (canRemoveFromBoard || canDelete) {
        if (canRemoveFromBoard) {
          if (options.length > 0 && options[options.length - 1]?.type !== 'separator') {
            options.push({ id: 'remove-separator', type: 'separator' });
          }

          options.push({
            id: 'remove-from-board',
            label: 'Remove from this board',
            prefix: <MinusIcon size="sm" />,
            onSelect: async () => {
              try {
                if (!currentWorkspace?.id) {
                  return;
                }
                await updateCanvas({
                  workspaceId: currentWorkspace.id,
                  canvases: [{ canvasId: canvas.id, boardId: null }],
                });
                trackCanvasMoved({
                  canvas_id: canvas.id,
                  original_location: effectiveBoardId,
                  new_location: 'canvases',
                });
                showToast('Canvas removed from board');
              } catch (error) {
                showToast('There was an error updating the Canvas.');
                reportErrorToBugsnag({
                  error,
                  context: 'Failed to update canvas',
                  metadata: { canvasId: canvas.id },
                });
              }
            },
            type: 'item',
          });
        }

        if (canDelete) {
          if (options.length > 0 && options[options.length - 1]?.type !== 'separator') {
            options.push({ id: 'delete-separator', type: 'separator' });
          }

          options.push({
            id: 'delete',
            label: 'Delete from workspace',
            type: 'item',
            className: 'ads:text-error text-red-9',
            prefix: <Trash2Icon size="sm" />,
            onSelect: () => {
              showDeleteCanvasConfirmationModal({
                canvasIds: [canvas.id],
                boardId: effectiveBoardId,
                leaveAfterDelete,
              });
            },
          });
        }
      }

      return options;
    },
    [
      account?.id,
      boardId,
      currentWorkspace?.id,
      selectCanvas,
      showEditCanvasDetailsModal,
      showDeleteCanvasConfirmationModal,
      showMoveCanvasToBoardModal,
      showSharePrivateCanvasModal,
      showDuplicateCanvasModal,
      showToast,
      trackCanvasMoved,
      updateCanvas,
      getBoardPermissions,
      workspacePermissions,
    ],
  );

  return {
    getCanvasMenuOptionsCallback,
  };
};
